// E08-t1 — THE AUTHENTICATED SHELL.
//
// Forge f1: "Authenticated shell — org and KB switchers, role-aware surfaces".
// Forge q1 (archived): every surface sits behind membership at launch, so an
// unauthenticated caller gets a sign-in prompt and NO app chrome — not hidden
// chrome, absent chrome: an org list is itself a disclosure.
// Forge q2 (archived): the MVP is three pages. Browser, History, Change Review
// and Work are deferred, and this file is where that fence is actually held —
// a surface that is never linked is a surface no one lands on.
// Forge f2: no free-form document editor anywhere; the chrome therefore carries
// no textarea and nothing contenteditable.
//
// E14-t5 ADDED A SECOND FENCE, of a different kind. Agent Chat is a BUILT page
// contract that this tier does not sell, so its nav entry is gated on
// `showAgentSurfaces` — one flag, src/tier.ts, passed by src/App.tsx. That is
// not the q2 fence above: a deferred surface is one nobody designed, and a
// hidden one is one this tier chooses not to offer. The route stays reachable.
//
// ROLE-AWARENESS IS PER ORG, NOT PER USER. git-host/src/serve.ts resolveActor()
// reads the role from `memberships` keyed on (org_id, user_id), and
// platform/src/guards.ts gives 'govern' — and with it invite-member,
// remove-member and set-guards, which ARE the Admin page — to `admin` alone.
// So the chrome asks: what is my role in the ACTIVE org? Admin in one org and
// viewer in another is an ordinary state, and switching orgs changes the nav.
//
// The shell OFFERS only what the guard would allow. Hiding the entry is not the
// enforcement (the Worker refuses regardless, naming the guard — Tenancy and
// Guards s2/s4); it is the honesty: a maintainer is not walked to a door that
// will be shut in their face.
//
// This component is deliberately presentational — session in, callbacks out.
// The container decides how the session is fetched and what "select an org"
// does to the URL, which keeps the shell testable with no network and no
// timers.
// E14-t6 — THE CHROME NO LONGER RE-IMPLEMENTS CONTROLS. The brand line, the role
// chip, the two buttons and the nav links were hand-built out of tokens; they are
// now Text, Badge, Button and Link from @astryxdesign/core. What shell.css still
// owns after that is layout this app invented (the three-row grid, the bar, the
// nav strip, the main padding).
//
// E14-t6, ON A SECOND PASS, FINISHED THE JOB AND COLLAPSED A DUPLICATE. The two
// switchers were the last hand-built controls in the shell — raw `<select>`s
// wearing .dj-select — and the first pass kept them for one reason only:
// shell.test.tsx pinned them AS
// native selects (cast to HTMLSelectElement, `.value`, `getAllByRole('option')`
// as HTMLOptionElement, fireEvent.change). That contract was rewritten, on
// purpose and on the record, to pin BEHAVIOUR instead of markup — what a person
// can see and do, not which element implements it — so both switchers are now
// the design system's `Selector`.
//
// THE ORGANIZATION SWITCHER LIVES HERE AND NOWHERE ELSE. Home used to carry its
// own, labelled "Viewing organization" only so that two controls would not share
// one accessible name (E14 t2 flagged the duplication and left it to this
// ticket). An organization applies to every page, not to one, so the global
// chrome is where it belongs: Home's control is gone, its org CONTEXT — signed
// in as, the org name, the role and what that role grants — stays, and the
// reason the control does not move for most accounts (sign-up grants a personal
// org that admits no second member) moved onto this Selector's `disabledMessage`
// rather than being lost with it.
import type { ThemeMode } from '../theme-mode';
import { useState, type ReactNode } from 'react';

import { AppShell as AstryxAppShell } from '@astryxdesign/core/AppShell';
import { Avatar } from '@astryxdesign/core/Avatar';
import { Badge } from '@astryxdesign/core/Badge';
import { Button } from '@astryxdesign/core/Button';
import { DropdownMenu } from '@astryxdesign/core/DropdownMenu';

import { Card } from '@astryxdesign/core/Card';
import { Heading } from '@astryxdesign/core/Heading';
import { Collapsible } from '@astryxdesign/core/Collapsible';
import { MobileNav } from '@astryxdesign/core/MobileNav';
import { SegmentedControl, SegmentedControlItem } from '@astryxdesign/core/SegmentedControl';
import { TextInput } from '@astryxdesign/core/TextInput';
import { SideNav, SideNavItem, SideNavSection } from '@astryxdesign/core/SideNav';
import { Stack } from '@astryxdesign/core/Stack';
import { Text } from '@astryxdesign/core/Text';
import { TopNav } from '@astryxdesign/core/TopNav';

import { Eyebrow } from '../components/Eyebrow';
import { identityColor } from '../identity';

import './shell.css';

/** platform/src/guards.ts — the three roles, verbatim. */
export type Role = 'admin' | 'maintainer' | 'viewer';

/** An org the signed-in person belongs to, with THEIR role in it. */
export interface OrgSummary {
  id: string;
  name: string;
  role: Role;
}

/** A KB, tagged with the NAME of the org that holds it. */
export interface KbSummary {
  id: string;
  name: string;
  org: string;
}

/** What the app knows about the caller. `null` means unauthenticated. */
export interface SessionPayload {
  user: { id: string; email: string };
  orgs: OrgSummary[];
  kbs: KbSummary[];
  /** an org NAME — the value the switcher shows */
  activeOrg: string | null;
  /** a kb NAME */
  activeKb: string | null;
}

export interface AppShellProps {
  session: SessionPayload | null;
  onSelectOrg?: (orgName: string) => void;
  onSelectKb?: (kbName: string) => void;
  onSignIn?: () => void;
  /** Tenancy and Guards s4, on the one channel a browser has. Offered only when
   *  the container supplies it, so the shell never shows a control that leads
   *  nowhere — which is exactly what "Sign in" was until E12-t29. */
  onSignOut?: () => void;
  /** E14-t5 — THE TIER GATE, and the only reason this prop exists. `false` means
   *  the Agent Chat entry is not BUILT: not disabled, not greyed, absent. The
   *  decision is src/tier.ts's and the container's to pass; the default keeps
   *  this component saying what E08-t1 built it to say. */
  showAgentSurfaces?: boolean;
  /** E15 q1 — answered "Ship a toggle", not the recommended "follow the OS".
   *  The mode lives at the root beside `<Theme>` (src/main.tsx); the shell only
   *  SHOWS it and asks for the next one, so the chrome owns no theme state. Both
   *  are optional: a shell rendered without them shows no control at all rather
   *  than a dead one. */
  themeMode?: ThemeMode;
  /** the picker's write (2026-08-07 verdict: a choice among three, never a
   *  cycling label) — absent, the menu shows no Theme section at all */
  onSelectThemeMode?: (mode: ThemeMode) => void;
  /** create an organization (the drawer's org picker carries the action —
   *  2026-08-07 verdict). Rejections are relayed into the drawer's own form;
   *  absent, the action is absent, never dead. */
  onCreateOrg?: (name: string) => Promise<void>;
  /** E21-t2 — the pathname the reader is ON, so the rail can mark it. Optional
   *  and presentation-only: absent (as in every pre-existing test) no entry is
   *  marked, and navigation still works — selection is paint, not routing. */
  currentPath?: string;
  /** In-app navigation, handed UP like every other decision this shell does not
   *  own. When supplied, a PLAIN left-click on a nav entry goes through it (the
   *  container's pushState routing) instead of a full document load — which is
   *  what made every click re-boot the app, re-ask /api/session, and flash the
   *  members-only card at a signed-in person (reported 2026-08-06). The `href`
   *  stays on the anchor, so middle-click, cmd-click and copy-link keep their
   *  browser meanings, and without the callback the anchor works as before. */
  onNavigate?: (href: string) => void;
  /** E25 — WHICH ALTITUDE this shell is drawn at (the container reads it off
   *  the route). 'org' is the organization workspace (the administrative
   *  rail); 'kb' is a knowledge base's workspace (ITS rail: Overview,
   *  Dashboards, Agent — with the way back up as the footer); 'dashboard' is
   *  the full-bleed published dashboard — NO rail at all (the dashboard's own
   *  navigation is the page), and the product heading grows the brand MENU
   *  that navigates back up. Default 'org', which is every pre-existing
   *  render. */
  altitude?: 'org' | 'kb' | 'dashboard';
  /** E25-t3 — one extra control in the bar, after the scope picker: the
   *  dashboard switcher on the dashboard altitude. The container owns what it
   *  is; the shell owns only where it sits. */
  topExtra?: ReactNode;
  /** the CURRENT dashboard's name — on mobile the bar hides the switcher
   *  (Felipe's verdict, 2026-08-07) and the brand menu is where the name
   *  lives, so the menu needs it said. */
  dashboardLabel?: string;
  /** E37 PR10 — the inbox's unread count, worn as a badge on its nav entry.
   *  The NUMBER is the container's (it reads GET /api/inbox and the Inbox page
   *  hands fresh counts up); the shell only shows it. Absent or zero, the
   *  entry is unadorned — a zero badge is noise wearing a uniform. */
  inboxUnread?: number;
  children?: ReactNode;
}

/** E14-t5. One option today, and it is a tier decision rather than a preference
 *  — see src/tier.ts for what it hides and why. */
export interface NavOptions {
  /** offer vault/Apps/Forge/Pages/Agent Chat.md at all */
  agentChat?: boolean;
}

/** The MVP routes, each owned by a page contract. Deferred surfaces (Forge q2:
 *  Browser, History, Change Review, Work) are absent from this table rather than
 *  filtered out of it — there is no entry to accidentally re-enable.
 *
 *  Agent Chat is the ONE exception, and it is an exception on purpose: it is a
 *  built page contract that this tier does not sell (E14 t5), not a surface that
 *  was never designed, so it is written down and gated rather than deleted. */
export function navEntriesFor(
  activeOrg: string | null,
  activeKb: string | null,
  role: Role | null,
  options: NavOptions = {},
): ReadonlyArray<{ name: string; href: string }> {
  const { agentChat = true } = options;
  const entries: Array<{ name: string; href: string }> = [];
  // DocujointOrg.dc.html (imported 2026-08-06) — the rail leads with the ORG's
  // shelf, not one KB's view: "Knowledge bases" replaces the Dashboards entry,
  // per the verdict on the imported design. Home IS that screen (src/pages/
  // Home.tsx renders KnowledgeBases per org), and a KB's dashboard is reached
  // the way the design reaches it: through its card. The Dashboards PAGE is
  // untouched — the route parses and renders; this rail just stopped being the
  // door to it.
  if (activeOrg) {
    entries.push({ name: 'Knowledge bases', href: '/' });
  }
  // E37 PR10 — the inbox is the SESSION's surface, not an org's: one list of
  // every mention of the reader across every org and KB they belong to, so no
  // scope gates the entry and the href carries no org.
  entries.push({ name: 'Inbox', href: '/inbox' });
  if (activeOrg && activeKb) {
    // vault/Apps/Forge/Pages/Agent Chat.md — E14-t5 hides this at the current
    // tier. The route still parses and still renders (src/App.tsx), so a url
    // somebody already holds keeps working; nothing OFFERS it.
    if (agentChat) {
      entries.push({ name: 'Agent Chat', href: `/${activeOrg}/${activeKb}/agent` });
    }
  }
  // vault/Apps/Forge/Pages/Reports.md — E22-t2. Org-scoped and offered to
  // EVERY member: the list is a read the Worker's generic guard already
  // admits, and the consent buttons inside are drawn for govern alone.
  if (activeOrg) {
    entries.push({ name: 'Reports', href: `/${activeOrg}/reports` });
  }
  // vault/Apps/Forge/Pages/Admin.md — govern is admin-only (guards.ts), and the
  // page is org-scoped, so it needs no active KB.
  if (activeOrg && role === 'admin') {
    // The administrative tail, in the DESIGN'S sequence (2026-08-07 design
    // audit): Usage & billing, Appearance, Admin.
    // vault/Apps/Forge/Pages/Billing.md f3 — E23-t2's first slice. Same gate and
    // the same honesty rule as Admin: usage-and-caps is an administrative
    // surface, so the entry is absent for other roles rather than disabled, and
    // the Worker refuses regardless of what the chrome offered.
    entries.push({ name: 'Usage & billing', href: `/${activeOrg}/billing` });
    // Appearance (Pages/Appearance.md, DocujointOrg.dc.html) — org theme
    // defaults: governance, so the same gate and the same honesty rule.
    entries.push({ name: 'Appearance', href: `/${activeOrg}/appearance` });
    entries.push({ name: 'Admin', href: `/${activeOrg}/settings` });
  }
  return entries;
}

/** E25-t1 — the KB WORKSPACE's own rail. Reports stay at the org altitude
 *  (consent is the org's); the workspace page carries the KB's reports panel
 *  and links up. The way back to the organization is the FOOTER, not an entry
 *  lost in the list — one gesture, always in the same place. */
export function kbNavEntriesFor(
  activeOrg: string | null,
  activeKb: string | null,
  options: NavOptions = {},
): ReadonlyArray<{ name: string; href: string }> {
  const { agentChat = true } = options;
  if (!activeOrg || !activeKb) return [];
  const entries: Array<{ name: string; href: string }> = [
    { name: 'Overview', href: `/${activeOrg}/${activeKb}` },
    { name: 'Dashboards', href: `/${activeOrg}/${activeKb}/dashboards` },
  ];
  if (agentChat) entries.push({ name: 'Agent Chat', href: `/${activeOrg}/${activeKb}/agent` });
  return entries;
}

/** The role held in the ACTIVE org — the only role that governs this chrome. */
export function activeRole(session: SessionPayload): Role | null {
  const org = session.orgs.find((candidate) => candidate.name === session.activeOrg);
  return org ? org.role : null;
}

/** E21-t2 — the rail's glyphs, drawn inline the way the adopted design draws
 *  its own: stroke-only 24-box paths, colored by currentColor so the theme owns
 *  them. `aria-hidden` because the LABEL is the accessible name — an icon that
 *  also announced would say everything twice. Keyed by nav entry NAME, and an
 *  entry with no glyph simply renders without one: adding a surface must never
 *  require adding art first. */
function navIcon(name: string): ReactNode {
  const paths: Record<string, string[]> = {
    // knowledge bases: the design's vault cube (DocujointOrg.dc.html I.vault)
    'Knowledge bases': ['M4 7.5 12 3.5l8 4v9L12 20.5 4 16.5Z', 'M4 7.5 12 11.5l8-4', 'M12 11.5v9'],
    // kb workspace overview: the design's vault cube, not the home glyph —
    // the KB rail leads with the KB itself (2026-08-07 design audit)
    Overview: ['M4 7.5 12 3.5l8 4v9L12 20.5 4 16.5Z', 'M4 7.5 12 11.5l8-4', 'M12 11.5v9'],
    // dashboards: the design's masonry tiles (2026-08-07 design audit), not a
    // uniform four-tile grid
    Dashboards: ['M4 5.5h7v6H4z', 'M13 5.5h7v9h-7z', 'M4 13.5h7v5H4z', 'M13 16.5h7v2h-7z'],
    // the way back up: a left arrow
    Organization: ['M20 12H4', 'M10 6l-6 6 6 6'],
    // the picker's current-entry check (the switcher's own grammar)
    Current: ['M20 6 9 17l-5-5'],
    // the create action: a plus
    'Create organization': ['M12 5.5v13', 'M5.5 12h13'],
    'All dashboards': ['M4 5.5h7v6H4z', 'M13 5.5h7v9h-7z', 'M4 13.5h7v5H4z', 'M13 16.5h7v2h-7z'],
    // organization overview leads to the org's shelf — the vault cube
    'Organization overview': ['M4 7.5 12 3.5l8 4v9L12 20.5 4 16.5Z', 'M4 7.5 12 11.5l8-4', 'M12 11.5v9'],
    // inbox: the tray
    Inbox: ['M4 13.5h4.5l1.5 2.5h4l1.5-2.5H20', 'M6 5.5h12l2 8v5H4v-5z'],
    // sign out: the door with the arrow through it
    'Sign out': ['M14 7.5V5.5H5v13h9v-2', 'M11 12h9', 'M17 9l3 3-3 3'],
    // agent: a speech bubble
    'Agent Chat': ['M4 5.5h16v10H9l-5 4v-4z'],
    // reports: bars on a baseline — the run/rollup glyph
    Reports: ['M4 19.5h16', 'M7 19.5V11', 'M12 19.5V5.5', 'M17 19.5v-5.5'],
    // appearance: the design's palette
    Appearance: [
      'M12 3.5a8.5 8.5 0 1 0 0 17c1.4 0 2-.8 2-1.7 0-.8-.5-1.2-.5-2 0-1 .8-1.8 2-1.8h1.6a3.4 3.4 0 0 0 3.4-3.5c0-4.4-3.8-8-8.5-8Z',
      'M7.5 12h.01', 'M10 8.5h.01', 'M14 8.5h.01', 'M16.5 12h.01',
    ],
    // admin: two sliders
    Admin: ['M3.5 8h17', 'M3.5 16h17', 'M9 5v6', 'M15 13v6'],
    // billing: a card with its magstripe
    'Usage & billing': ['M3 7.5h18v10H3z', 'M3 11h18', 'M6.5 14.5h4'],
  };
  const d = paths[name];
  if (!d) return undefined;
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {d.map((path) => (
        <path key={path} d={path} />
      ))}
    </svg>
  );
}

/** The designs' identity dot — one hue per named entity, computed, never stored. */
function IdentityDot({ name, size = 8 }: { name: string; size?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        background: identityColor(name),
        display: 'block',
        flex: '0 0 auto',
      }}
    />
  );
}

function SignInPrompt({ onSignIn }: { onSignIn?: () => void }) {
  return (
    <div className="dj-signin" data-surface="signin">
      {/* E15-t5 — `elevation="none"`. The prototype defines one shadow token and
          then spends it on nothing: a card is bordered, not floated. Stated
          rather than left to the default so a future reader can see the
          decision instead of inheriting it. */}
      <Card padding={6} elevation="none" maxWidth="26rem" width="100%">
        <Stack direction="vertical" gap={3}>
          {/* E15-t2 — the product's name over the prompt is an EYEBROW: it names
              the surface without competing with the title under it, which is
              exactly what this line was already doing at `supporting`. */}
          <Eyebrow>docujoint forge</Eyebrow>
          <Heading level={1} type="display-3">
            Members only
          </Heading>
          <Text type="body" color="secondary">
            Every knowledge base in the forge sits behind membership. Sign in to reach your
            organizations.
          </Text>
          <Button type="button" variant="primary" width="100%" label="Sign in" onClick={onSignIn} />
        </Stack>
      </Card>
    </div>
  );
}

export function AppShell({
  session,
  onSelectOrg,
  onSelectKb,
  onSignIn,
  onSignOut,
  showAgentSurfaces = true,
  children,
  themeMode,
  onSelectThemeMode,
  currentPath,
  onNavigate,
  altitude = 'org',
  topExtra,
  dashboardLabel,
  inboxUnread,
  onCreateOrg,
}: AppShellProps) {
  // THE MENU IS A DRAWER (Felipe's verdict, 2026-08-07): local UI state, held
  // before the unauthenticated early-return because hooks may not be
  // conditional.
  const [menuOpen, setMenuOpen] = useState(false);
  const [orgDraft, setOrgDraft] = useState<{ open: boolean; name: string; busy: boolean; error: string | null }>({
    open: false,
    name: '',
    busy: false,
    error: null,
  });

  // Unauthenticated: the prompt IS the page. No nav landmark, no switcher, no
  // org names on the wire.
  if (session === null) return <SignInPrompt onSignIn={onSignIn} />;

  const role = activeRole(session);
  const kbsInActiveOrg = session.kbs.filter((kb) => kb.org === session.activeOrg);
  // E25 — the rail is the ALTITUDE's: the org's administrative rail on org
  // routes, the KB's own on KB routes. The dashboard altitude draws none.
  const entries =
    altitude === 'kb'
      ? kbNavEntriesFor(session.activeOrg, session.activeKb, { agentChat: showAgentSurfaces })
      : navEntriesFor(session.activeOrg, session.activeKb, role, {
          agentChat: showAgentSurfaces,
        });

  // The adopted design's chrome (Docujoint.dc.html, imported 2026-08-06) is the
  // design system's OWN application frame: `AppShell variant="elevated"` — wash
  // nav surfaces around an elevated, radiused content area — with a slotted
  // TopNav over a sectioned, collapsible SideNav. The hand-rolled three-area
  // grid this file carried (and shell.css skinned) said the same thing in the
  // app's private vocabulary; it is deleted rather than kept as a second way to
  // draw one frame. Every control INSIDE the slots is unchanged — the same two
  // Selectors, the same actor row, the same entries — so every pinned by-name
  // query still reaches what it always reached.

  /* THE SCOPE PICKERS — the imported designs' grammar (DocujointOverview/Kb,
     2026-08-06): two ghost DropdownMenus wearing the active entity's IDENTITY
     DOT, joined by the aria-hidden slash. They replaced the two labelled
     Selectors ON THE RECORD: what a picker IS changed (a menu of named places,
     not a form field), while what it DOES is byte-identical — the same
     onSelectOrg/onSelectKb, handed the same NAMES. The sole-org
     disabledMessage retired with the Selector: a menu of one org simply shows
     the one org.

     E14-t6's rule survives the change of clothes: these are THE organization
     and knowledge-base controls in the product, and they live here alone. */
  /* THE BAR'S ONE REMAINING PICKER — the knowledge base. The ORG picker
     moved into the drawer (Felipe's verdict, 2026-08-07): switching
     organizations is a menu-altitude act now, beside the places it changes.
     The display:contents wrapper exists for the TESTS' reach alone. */
  const scopePicker = (
    <Stack direction="horizontal" gap={1} vAlign="center">
      <span data-testid="shell-kb-switcher" style={{ display: 'contents' }}>
      <DropdownMenu
        hasChevron
        button={{
          label: session.activeKb ?? 'All knowledge bases',
          variant: 'ghost',
          size: 'sm',
          icon: session.activeKb ? <IdentityDot name={session.activeKb} /> : undefined,
        }}
        items={
          kbsInActiveOrg.length > 0
            ? kbsInActiveOrg.map((kb) => ({
                label: kb.name,
                icon: <IdentityDot name={kb.name} />,
                onClick: () => onSelectKb?.(kb.name),
              }))
            : [
                {
                  label: `${session.activeOrg ?? 'This organization'} holds no knowledge base you can reach.`,
                  isDisabled: true,
                },
              ]
        }
      />
      </span>
    </Stack>
  );

  /* IDENTITY, NOT A DOOR (2026-08-07 verdict): the bar's end says WHO — the
     role chip and the avatar — and opens nothing. Every act that lived behind
     the avatar is in the ONE menu, on the wordmark. */
  const actorRow = (
    <Stack direction="horizontal" gap={2} vAlign="center" className="dj-actor">
      {role ? <Badge label={role} /> : null}
      <Avatar name={session.user.email} size={24} />
    </Stack>
  );

  // E37 PR10 — the unread count rides the Inbox entry as endContent, shown
  // only when it is a number worth saying (a zero badge is noise).
  const inboxBadge = (entry: { href: string }) =>
    entry.href === '/inbox' && typeof inboxUnread === 'number' && inboxUnread > 0 ? (
      <span data-testid="inbox-unread">
        <Badge label={String(inboxUnread)} variant="info" />
      </span>
    ) : undefined;

  const item = (entry: { name: string; href: string }) => (
    <SideNavItem
      key={entry.name}
      label={entry.name}
      href={entry.href}
      icon={navIcon(entry.name)}
      endContent={inboxBadge(entry)}
      isSelected={currentPath !== undefined && currentPath === entry.href}
      onClick={
        onNavigate &&
        ((e) => {
          // Only a PLAIN left-click is the app's to route. Modified
          // clicks and non-primary buttons are the BROWSER's grammar
          // (new tab, new window) and the href serves them untouched.
          if (e.defaultPrevented || e.button !== 0) return;
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          onNavigate(entry.href);
        })
      }
    />
  );

  /* The one navigation landmark NAMED PRIMARY — the design system's SideNav
     roots a <div>, so the <nav> is the app's wrapper, exactly as before: the
     landmark contract ("navigation, named Primary, holding the entries as
     links") outlives any component's choice of root element. The TopNav is its
     own landmark with its own name; "Primary" stays unique. Selection is paint
     from `currentPath` (absent, no entry is marked), never a routing
     decision. */
  // E25-t1 — the KB workspace's footer IS the way back up: one entry, always
  // in the same place, drawn with the back arrow.
  const kbFooter = item({ name: 'Organization', href: '/' });

  /* THE RAILS, per the imported designs (2026-08-06):
     - org altitude: the ICON RAIL — collapsed by default, labels as the
       links' accessible names (and the expand button one keypress away).
       No section title: the rail IS the organization's.
     - kb altitude: EXPANDED, headed with the KB's identity (dot, mono name,
       the KNOWLEDGE BASE eyebrow), Organization pinned as the footer.
     - dashboard altitude: none — the dashboard owns the viewport. */
  const sideNav =
    altitude === 'dashboard' ? undefined : (
      <nav className="dj-nav" aria-label="Primary">
        {altitude === 'kb' ? (
          // No identity header: the bar's KB picker already names the base
          // (Felipe's verdict, 2026-08-07 phone review — "remove this card,
          // is redundant as we have the dropdown").
          <SideNav collapsible={{ hasButton: true }} footer={kbFooter}>
            {entries.map(item)}
          </SideNav>
        ) : (
          // The org-altitude rail is ONE flat run — no footer split (2026-08-07
          // design audit): the administrative tail rides the same list, in
          // navEntriesFor's order. The KB altitude's footer stays: that one IS
          // per design (the way back up, always in the same place).
          <SideNav collapsible={{ defaultIsCollapsed: true, hasButton: true }}>
            {entries.map(item)}
          </SideNav>
        )}
      </nav>
    );

  /* THE BRAND MENU — at EVERY altitude now (the imported designs hang it on
     the product heading itself, as a DropdownMenu): the way back up when
     there is an up, the administrative doors for the governing role, and
     Sign out. Every entry is an act this session can take; none is
     decoration.

     STRUCTURED as the design draws it (2026-08-07 design audit): titled
     sections, not a flat run — the KB's acts under 'This knowledge base' on
     the dashboard altitude, the organization's under the org's own name —
     and every entry wears its glyph, so the back chevron carries the arrow
     the old '← ' label prefix faked. */
  /* THE DRAWER — the one menu, as a side panel (Felipe's verdict,
     2026-08-07: "convert this menu to a drawer and move the org dropdown
     inside it"). MobileNav drives a native <dialog>: backdrop, escape and
     focus are the platform's. Its sections, top to bottom:

       ORGANIZATION   the org PICKER — every org as a row wearing its
                      identity dot, the active one marked (aria-current)
       THE ALTITUDE   the rail's own entries under the place's name — the
                      KB's on KB routes, the org's everywhere (deduplicated
                      by construction: one builder, one render)
       THEME          the three modes as a PICKER, active marked — never a
                      cycling label
       THE ACCOUNT    the email as the heading, Sign out as the act

     Every act closes the drawer before it navigates — a menu that lingers
     over the page it changed reads as a menu that did nothing. */
  const act = (run: () => void) => () => {
    setMenuOpen(false);
    run();
  };

  const drawerEntry = (entry: { name: string; href: string }) => (
    <SideNavItem
      key={entry.name}
      label={entry.name}
      icon={navIcon(entry.name)}
      endContent={inboxBadge(entry)}
      isSelected={currentPath !== undefined && currentPath === entry.href}
      onClick={act(() => onNavigate?.(entry.href))}
    />
  );

  // The org's entries, offered at EVERY altitude (the command-center grammar)
  // — minus Agent Chat, which is the KB section's own entry when it applies.
  const orgEntries = navEntriesFor(session.activeOrg, session.activeKb, role, {
    agentChat: showAgentSurfaces,
  }).filter((entry) => entry.name !== 'Agent Chat');

  /* THE ORG PICKER IS A DROPDOWN INSIDE THE DRAWER (2026-08-07 verdict) — a
     Collapsible whose trigger wears the ACTIVE org (dot · name · chevron is
     the component's own), expanding IN PLACE (a popover would hide under the
     dialog's top layer): the other orgs as rows, then Create organization,
     whose form is the drawer's own two lines. Only the current PAGE row
     highlights in this drawer — active facts (org, theme) live on their
     controls, not as more pills. */
  const orgPicker = (
    <Collapsible
      className="dj-drawer-org"
      defaultIsOpen={false}
      trigger={
        <Stack direction="horizontal" gap={2} vAlign="center">
          {session.activeOrg ? <IdentityDot name={session.activeOrg} /> : null}
          <Text type="label" weight="semibold">
            {session.activeOrg ?? 'Select an organization'}
          </Text>
        </Stack>
      }
    >
      <Stack direction="vertical" gap={0}>
        {session.orgs
          .filter((org) => org.name !== session.activeOrg)
          .map((org) => (
            <SideNavItem
              key={org.name}
              label={org.name}
              icon={<IdentityDot name={org.name} />}
              onClick={act(() => onSelectOrg?.(org.name))}
            />
          ))}
        {onCreateOrg ? (
          orgDraft.open ? (
            <form
              className="dj-drawer-create"
              onSubmit={(event) => {
                event.preventDefault();
                const name = orgDraft.name.trim();
                if (name === '' || orgDraft.busy) return;
                setOrgDraft((d) => ({ ...d, busy: true, error: null }));
                void onCreateOrg(name)
                  .then(() => {
                    setOrgDraft({ open: false, name: '', busy: false, error: null });
                    setMenuOpen(false);
                  })
                  .catch((error: Error) => {
                    setOrgDraft((d) => ({ ...d, busy: false, error: error.message }));
                  });
              }}
            >
              <TextInput
                label="Organization name"
                value={orgDraft.name}
                onChange={(name) => setOrgDraft((d) => ({ ...d, name }))}
                size="sm"
                width="100%"
                isDisabled={orgDraft.busy}
              />
              {orgDraft.error ? (
                <Text type="code" color="secondary">
                  {orgDraft.error}
                </Text>
              ) : null}
              <Stack direction="horizontal" gap={2}>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  label="Create"
                  isLoading={orgDraft.busy}
                  isDisabled={orgDraft.name.trim() === ''}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  label="Cancel"
                  isDisabled={orgDraft.busy}
                  onClick={() => setOrgDraft({ open: false, name: '', busy: false, error: null })}
                />
              </Stack>
            </form>
          ) : (
            <SideNavItem
              label="Create organization"
              icon={navIcon('Create organization')}
              onClick={() => setOrgDraft({ open: true, name: '', busy: false, error: null })}
            />
          )
        ) : null}
      </Stack>
    </Collapsible>
  );

  const menuDrawer = (
    <MobileNav
      isOpen={menuOpen}
      onOpenChange={setMenuOpen}
      side="start"
      className="dj-drawer"
      data-testid="shell-menu"
    >
      <SideNavSection title="Organization">{orgPicker}</SideNavSection>

      {altitude === 'dashboard' && session.activeOrg && session.activeKb ? (
        <SideNavSection title="This knowledge base">
          {dashboardLabel ? (
            // WHERE YOU ARE — a fact, not an act (mobile's only home for the
            // dashboard's name; the bar hides the switcher there).
            <SideNavItem label={dashboardLabel} icon={navIcon('All dashboards')} isDisabled />
          ) : null}
          {/* no "Back to knowledge base" row — All dashboards IS the way back
              (Felipe, 2026-08-07: "redundant as clicking dashboards takes me
              back"), and the KB overview stays one hop away from there */}
          <SideNavItem
            label="All dashboards"
            icon={navIcon('All dashboards')}
            onClick={act(() =>
              onNavigate?.(`/${session.activeOrg}/${session.activeKb}/dashboards`),
            )}
          />
        </SideNavSection>
      ) : null}

      {altitude === 'kb' && session.activeKb ? (
        <SideNavSection title={session.activeKb}>{entries.map(drawerEntry)}</SideNavSection>
      ) : null}

      {orgEntries.length > 0 ? (
        <SideNavSection title={session.activeOrg ?? 'Organization'}>
          {(altitude === 'org' ? entries : orgEntries).map(drawerEntry)}
        </SideNavSection>
      ) : null}

      {themeMode && onSelectThemeMode ? (
        <SideNavSection title="Theme">
          {/* one control, one selection — a segmented picker instead of three
              rows fighting the nav rows for highlight (2026-08-07 verdict) */}
          <div className="dj-drawer-theme">
            <SegmentedControl
              label="Theme"
              value={themeMode}
              onChange={(mode) => onSelectThemeMode(mode as ThemeMode)}
              size="sm"
            >
              <SegmentedControlItem value="system" label="System" />
              <SegmentedControlItem value="light" label="Light" />
              <SegmentedControlItem value="dark" label="Dark" />
            </SegmentedControl>
          </div>
        </SideNavSection>
      ) : null}

      <SideNavSection title={session.user.email}>
        {onSignOut ? (
          <SideNavItem label="Sign out" icon={navIcon('Sign out')} onClick={act(onSignOut)} />
        ) : null}
      </SideNavSection>
    </MobileNav>
  );

  const topNav = (
    <TopNav
      label="docujoint"
      heading={
        // the wordmark opens THE DRAWER — same accessible name every pin
        // already reaches for
        <Button
          type="button"
          variant="ghost"
          size="sm"
          label="docujoint"
          onClick={() => setMenuOpen(true)}
        />
      }
      startContent={
        // THE DASHBOARD ALTITUDE'S BAR IS THE SLIM ONE (Felipe's verdict,
        // 2026-08-07 phone review): the brand menu already holds every way
        // back up, so the scope pickers are redundant there — that bar
        // carries the ONE picker that adds function on the page (the
        // dashboard switcher). Every other altitude keeps the full scope
        // picker, with the audit's hairlines between the groups.
        altitude === 'dashboard' ? (
          topExtra ? (
            <Stack direction="horizontal" gap={3} vAlign="center" className="dj-bar-dash">
              <span className="dj-bar-separator" aria-hidden="true" />
              {topExtra}
            </Stack>
          ) : undefined
        ) : (
          <Stack direction="horizontal" gap={3} vAlign="center">
            <span className="dj-bar-separator" aria-hidden="true" />
            {scopePicker}
            {topExtra ? (
              <>
                <span className="dj-bar-separator" aria-hidden="true" />
                {topExtra}
              </>
            ) : null}
          </Stack>
        )
      }
      endContent={
        // …and its end says only WHO: the avatar, without the role chip — the
        // role governs offers, and this altitude offers through the menu.
        altitude === 'dashboard' ? <Avatar name={session.user.email} size={24} /> : actorRow
      }
    />
  );

  return (
    <AstryxAppShell
      variant="elevated"
      height="fill"
      contentPadding={0}
      topNav={topNav}
      sideNav={sideNav}
      // The drawer RETIRED the same day it arrived (2026-08-07 verdict): the
      // brand menu carries the altitude's rail entries, so mobile's whole nav
      // is the one menu and no hamburger competes with it.
      mobileNav={false}
      data-surface="shell"
    >
      {/* The dashboard altitude BLEEDS: the artifact owns every pixel under
          the bar, so the page padding is the other altitudes' alone. */}
      <div className={altitude === 'dashboard' ? 'dj-main dj-main--bleed' : 'dj-main'}>
        {children}
      </div>
      {menuDrawer}
    </AstryxAppShell>
  );
}
